import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as ArrowDownIcon } from "common/assets/icons/chevron-down.svg";
import { SelectProps } from "./Select.props";
import { SelectItemProps } from "./SelectItem";
import { P } from "common/P/P";

export const Select: React.FC<SelectProps> = ({
	label,
	children,
	value,
	defaultSelectLabel = "Select property",
	className,
	disabled = false,
	onSelect,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedLabel, setSelectedLabel] = useState(defaultSelectLabel);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Update the selectedLabel when the value prop changes
		setSelectedLabel(value ? value : defaultSelectLabel);
	}, [value, defaultSelectLabel]);

	const getStatusClass = (status: string) => {
		if (typeof status !== "string") {
			return "";
		}

		const loweredStatus = status.toLowerCase();
		switch (loweredStatus) {
			case "preparing":
				return "text-[#BF310A]";
			case "delivered":
				return "text-[#2DAA6F]";
			case "return":
			case "returned":
				return "text-[#101828]";
			case "cancel":
			case "cancelled":
				return "text-[#F21414]";
			case "waiting":
				return "text-[#FE9A04]";
			default:
				return "";
		}
	};

	const isDefaultSelect = (itemValue: string): boolean => {
		if (itemValue === "Cancel" && defaultSelectLabel === "Cancelled")
			return true;
		if (itemValue === "Return" && defaultSelectLabel === "Returned")
			return true;
		if (itemValue === defaultSelectLabel) return true;
		return false;
	};

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const handleClickOutside = (e: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(e.target as Node)
		) {
			setIsOpen(false);
		}
	};

	const handleItemSelect = (selectedValue: string) => {
		setSelectedLabel(selectedValue);
		if (onSelect) {
			onSelect(selectedValue);
		}
		setIsOpen(false);
	};

	return (
		<div
			className={`relative grid gap-2 w-full ${disabled ? "opacity-60" : ""}`}
			ref={dropdownRef}
		>
			{label && (
				<P size="t1" type="regular" className="">
					{label}
				</P>
			)}
			<div
				onClick={() => !disabled && setIsOpen(!isOpen)}
				className={`grid items-center w-full text-sm font-medium rounded-md px-4 py-[10px] transition-all duration-300 ease-in-out border cursor-pointer ${
					disabled ? "cursor-not-allowed" : ""
				} grid-cols-frMax border-strokeColor bg-white duration-400 focus:outline-none ${getStatusClass(
					selectedLabel
				)}`}
			>
				<P
					size="t1"
					type="regular"
					className={`cursor-pointer ${
						selectedLabel === defaultSelectLabel
							? "text-grayTwo"
							: "text-mainFirstColor"
					}`}
				>
					{selectedLabel}
				</P>
				<ArrowDownIcon className="stroke-passiveTextColor" />
			</div>
			{isOpen && !disabled && (
				<div className="absolute z-10 w-full transition-all duration-300 bg-white border-b rounded-md shadow-lg border-x top-full border-strokeColor">
					{React.Children.map(children, (child) => {
						if (
							React.isValidElement<SelectItemProps>(child) &&
							child.props.value !== selectedLabel &&
							!isDefaultSelect(child.props.value)
						) {
							return React.cloneElement(child, {
								onSelect: handleItemSelect,
							});
						}
						return null;
					})}
				</div>
			)}
		</div>
	);
};
